import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Search, SlidersHorizontal, MoreVertical,
  Package, Star, Edit3, Trash2,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  listenToHostProducts,
  deleteProduct,
  Product,
} from "../../redux/productSlice";
import { fetchAllProductSummaries } from "../../redux/productRatingSlice";

/* ═══════════════════════════════════════════════════════════
   HEARTLAS DESIGN TOKENS — same palette as AtlasHome / SellerDashboard
═══════════════════════════════════════════════════════════ */
const HEART = {
  bg:        "#FBF6EE",
  cardBg:    "#FFFFFF",
  surface:   "#FCEFD4",
  offwhite:  "#FBF6EE",

  dark:      "#1B0E06",
  text:      "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",

  red:       "#C8202E",
  redDark:   "#9E1521",
  redSoft:   "#FBE7E9",

  gold:      "#F2A93E",
  goldDark:  "#D4900F",
  goldSoft:  "#FCEFD4",

  border:    "#EEDFC4",
  divider:   "#F3E6CE",

  green:     "#1FAA59",
  greenSoft: "rgba(31,170,89,.10)",
  danger:    "#C8202E",
};

const GRAD      = `linear-gradient(90deg, ${HEART.dark} 0%, ${HEART.gold} 35%, ${HEART.red} 65%, ${HEART.redDark} 100%)`;
const GRAD_CTA  = `linear-gradient(135deg, ${HEART.gold} 0%, ${HEART.red} 100%)`;
const GRAD_SIZE = "300% 100%";

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800;900&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
  *,*::before,*::after { box-sizing: border-box; margin: 0; padding: 0; }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-thumb { background: ${HEART.border}; border-radius: 8px; }
  input, button { font-family: inherit; }
  body { background: ${HEART.bg}; }

  @keyframes arSpin {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes pulseDot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: .5; transform: scale(1.4); }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  input::placeholder { color: ${HEART.textFaint}; }
  input:focus { outline: none; }

  .search-wrap { transition: border-color .15s, box-shadow .15s; }
  .search-wrap:focus-within {
    border-color: rgba(200,32,46,.45) !important;
    box-shadow: 0 0 0 3px rgba(200,32,46,.08);
  }

  .filter-btn { transition: background .15s, border-color .15s; }
  .filter-btn:hover { background: ${HEART.goldSoft} !important; border-color: rgba(200,32,46,.35) !important; }

  .add-btn { transition: transform .15s, box-shadow .15s, filter .15s; }
  .add-btn:hover { transform: translateY(-1px); filter: brightness(1.04); }

  /* ── product row hover ── */
  .p-row { transition: box-shadow .2s, transform .15s, border-color .2s; cursor: pointer; }
  .p-row:hover {
    box-shadow: 0 8px 28px rgba(200,32,46,0.10);
    border-color: rgba(242,169,62,.55) !important;
    transform: translateY(-2px);
  }

  /* ── dot menu dropdown ── */
  .dot-menu {
    position: absolute; right: 12px; top: 52px;
    background: #fff;
    border: 1.5px solid ${HEART.border};
    border-radius: 14px; overflow: hidden; z-index: 50;
    box-shadow: 0 14px 40px rgba(27,14,6,.16); min-width: 168px;
  }
  .dot-menu-item {
    display: flex; align-items: center; gap: 10px; padding: 11px 16px;
    font-size: 13px; font-weight: 600; color: ${HEART.text}; cursor: pointer;
    border: none; background: none; width: 100%; text-align: left;
    transition: background .15s; font-family: 'DM Sans', sans-serif;
  }
  .dot-menu-item:hover { background: ${HEART.goldSoft}; }
  .dot-menu-item.danger { color: ${HEART.danger}; }
  .dot-menu-item.danger:hover { background: ${HEART.redSoft}; }

  .stat-card { transition: border-color .15s, box-shadow .15s; }
  .stat-card:hover { border-color: rgba(200,32,46,.28); box-shadow: 0 4px 16px rgba(27,14,6,.06); }
`;

/* ─── helpers ─── */
const fmtPrice = (n: number) =>
  `€${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ─── ProductRow ─── */
interface RowCardProps {
  product:  Product;
  rating?:  { avg: number; count: number } | null;
  onEdit:   () => void;
  onDelete: () => void;
}

const ProductRow: React.FC<RowCardProps> = ({ product, rating, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const thumb = product.photos?.[0] ?? null;

  return (
    <motion.div
      className="p-row"
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      style={{
        position: "relative",
        background: HEART.cardBg,
        border: `1.5px solid ${HEART.border}`,
        borderRadius: 16,
        marginBottom: 12,
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: 12,
        overflow: "visible",
        boxShadow: "0 2px 12px rgba(27,14,6,0.04)",
      }}
      onClick={onEdit}
    >
      {/* Left gradient accent bar */}
      <div style={{
        position: "absolute", left: 0, top: 0, bottom: 0, width: 3,
        borderRadius: "16px 0 0 16px",
        background: GRAD, backgroundSize: GRAD_SIZE, animation: "arSpin 5s linear infinite",
      }} />

      {/* Thumbnail */}
      <div style={{
        width: 88, height: 88, borderRadius: 12, overflow: "hidden",
        background: `linear-gradient(135deg, ${HEART.goldSoft}, #FDF3E1)`,
        flexShrink: 0, position: "relative", marginLeft: 8,
        border: `1px solid ${HEART.border}`,
      }}>
        {thumb ? (
          <img src={thumb} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Package size={28} color={HEART.gold} style={{ opacity: 0.6 }} />
          </div>
        )}

        {/* Sold badge */}
        {(product as any).sold > 0 && (
          <div style={{
            position: "absolute", bottom: 5, left: 5,
            background: "rgba(27,14,6,0.78)", backdropFilter: "blur(6px)",
            borderRadius: 6, padding: "2px 7px",
            display: "flex", alignItems: "center", gap: 4,
          }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: HEART.gold, animation: "pulseDot 2s infinite" }} />
            <span style={{ fontSize: 9, fontWeight: 700, color: "#fff", fontFamily: "'Sora',sans-serif" }}>
              {(product as any).sold >= 1000
                ? `${((product as any).sold / 1000).toFixed(0)}K`
                : (product as any).sold} sold
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 800, fontSize: 15, color: HEART.text,
          marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis",
          whiteSpace: "nowrap", letterSpacing: -0.2,
          fontFamily: "'Sora', sans-serif",
        }}>
          {product.name || "Untitled Product"}
        </div>

        {(product as any).description && (
          <div style={{
            fontSize: 12, color: HEART.textMuted, marginBottom: 8,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            fontFamily: "'DM Sans', sans-serif",
          }}>
            {(product as any).description}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* price tag — squared, not pill */}
          <span style={{
            display: "inline-flex", alignItems: "center",
            background: GRAD_CTA,
            borderRadius: 8, padding: "4px 12px",
            fontWeight: 900, color: "#fff", fontSize: 13.5, letterSpacing: -0.2,
            fontFamily: "'Sora', sans-serif",
          }}>
            {fmtPrice(product.rate ?? 0)}
          </span>

          {rating && (
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <Star size={11} fill={HEART.gold} color={HEART.gold} />
              <span style={{ color: HEART.goldDark, fontWeight: 800, fontSize: 12, fontFamily: "'Sora',sans-serif" }}>
                {rating.avg.toFixed(1)}
              </span>
              <span style={{ color: HEART.textFaint, fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>
                · {rating.count.toLocaleString()} reviews
              </span>
            </div>
          )}
        </div>

        {/* Status badges — squared */}
        <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
          {product.isPublished ? (
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: "0.09em",
              background: HEART.greenSoft, color: HEART.green,
              padding: "2px 8px", borderRadius: 5,
              border: "1px solid rgba(31,170,89,.28)",
              fontFamily: "'Sora',sans-serif",
            }}>LIVE</span>
          ) : (
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: "0.09em",
              background: HEART.offwhite, color: HEART.textFaint,
              padding: "2px 8px", borderRadius: 5,
              border: `1px solid ${HEART.border}`,
              fontFamily: "'Sora',sans-serif",
            }}>DRAFT</span>
          )}
          {product.stock !== undefined && (
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: "0.07em",
              background: HEART.goldSoft, color: HEART.goldDark,
              padding: "2px 8px", borderRadius: 5,
              border: "1px solid rgba(212,144,15,.28)",
              fontFamily: "'Sora',sans-serif",
            }}>
              {product.stock} IN STOCK
            </span>
          )}
        </div>
      </div>

      {/* 3-dot button */}
      <button
        onClick={e => { e.stopPropagation(); setMenuOpen(v => !v); }}
        style={{
          background: "none", border: "none", cursor: "pointer",
          padding: 6, color: HEART.textFaint, flexShrink: 0,
        }}
      >
        <MoreVertical size={18} />
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <div
              style={{ position: "fixed", inset: 0, zIndex: 49 }}
              onClick={e => { e.stopPropagation(); setMenuOpen(false); }}
            />
            <motion.div
              className="dot-menu"
              initial={{ opacity: 0, scale: 0.92, y: -6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: -6 }}
              transition={{ duration: 0.15 }}
              onClick={e => e.stopPropagation()}
            >
              <button className="dot-menu-item" onClick={() => { setMenuOpen(false); onEdit(); }}>
                <Edit3 size={14} color={HEART.red} /> Edit Product
              </button>
              <button className="dot-menu-item danger" onClick={() => { setMenuOpen(false); onDelete(); }}>
                <Trash2 size={14} /> Delete
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN — Products
═══════════════════════════════════════════════════════════ */
const Products: React.FC = () => {
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const authUserId = useSelector((s: RootState) => s.auth.user?.userId);
  const hostId     = userId ?? authUserId ?? "";

  const products        = useSelector((s: RootState) => s.products.products);
  const ratingSummaries = useSelector((s: RootState) => s.productRatings.summaries);

  const [search, setSearch] = useState("");

  /* ── listeners ── */
  useEffect(() => {
    if (!hostId) return;
    dispatch(listenToHostProducts({ hostId }));
    dispatch(fetchAllProductSummaries());
  }, [dispatch, hostId]);

  /* ── filtered list ── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p: any) =>
      `${p.name ?? ""} ${p.description ?? ""}`.toLowerCase().includes(q)
    );
  }, [products, search]);

  const handleDelete = (listingId: string) => {
    if (!window.confirm("Delete this product permanently?")) return;
    dispatch(deleteProduct(listingId));
  };

  const goToAdd = (listingId?: string) =>
    navigate(listingId ? `/AddProducts/${listingId}` : "/AddProducts");

  const publishedCount = products.filter((p: any) => p.isPublished).length;
  const draftCount     = products.filter((p: any) => !p.isPublished).length;

  /* ─── RENDER ─── */
  return (
    <div style={{
      minHeight: "100vh",
      background: HEART.bg,
      fontFamily: "'DM Sans', sans-serif",
      paddingBottom: 48,
    }}>
      <style>{CSS}</style>

      {/* ══ PAGE HEADER ══ */}
      <div style={{
        background: HEART.cardBg,
        borderBottom: `1px solid ${HEART.border}`,
        padding: "0 20px 0",
        boxShadow: "0 2px 16px rgba(27,14,6,0.04)",
      }}>
        {/* Gradient top accent */}
        <div style={{
          height: 3,
          background: GRAD,
          backgroundSize: GRAD_SIZE,
          animation: "arSpin 5s linear infinite",
          marginLeft: -20, marginRight: -20,
        }} />

        <div style={{ paddingTop: 20, paddingBottom: 4 }}>
          {/* Title row */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
            <div>
              <h1 style={{
                fontWeight: 900, fontSize: 26, color: HEART.text,
                letterSpacing: -0.5, lineHeight: 1.1,
                fontFamily: "'Sora', sans-serif",
              }}>
                My Products
              </h1>
              <p style={{ fontSize: 13, color: HEART.textMuted, marginTop: 3 }}>
                Manage and monitor your listings
              </p>
            </div>

            {/* Add Product CTA — squared, not pill */}
            <motion.button
              className="add-btn"
              whileTap={{ scale: 0.96 }}
              onClick={() => goToAdd()}
              style={{
                display: "flex", alignItems: "center", gap: 7,
                background: GRAD_CTA,
                border: "none", borderRadius: 12,
                padding: "12px 20px",
                color: "#fff", fontWeight: 700, fontSize: 14,
                cursor: "pointer",
                boxShadow: "0 6px 20px rgba(200,32,46,.28)",
                flexShrink: 0,
                fontFamily: "'Sora', sans-serif",
              }}
            >
              <Plus size={16} /> Add Product
            </motion.button>
          </div>

          {/* Stat cards — squared, bordered, not pills */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, marginBottom: 18, maxWidth: 480 }}>
            {[
              { label: "Live",   val: publishedCount,  col: HEART.green },
              { label: "Drafts", val: draftCount,      col: HEART.textMuted },
              { label: "Total",  val: products.length, col: HEART.red },
            ].map(s => (
              <div key={s.label} className="stat-card" style={{
                display: "flex", flexDirection: "column", gap: 4,
                background: HEART.offwhite,
                border: `1.5px solid ${HEART.border}`,
                borderRadius: 12, padding: "10px 14px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: s.col, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: HEART.textMuted, fontWeight: 600 }}>{s.label}</span>
                </div>
                <span style={{ fontSize: 20, fontWeight: 900, color: HEART.text, fontFamily: "'Sora',sans-serif", letterSpacing: -0.3 }}>
                  {s.val}
                </span>
              </div>
            ))}
          </div>

          {/* Search + Filter — squared */}
          <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
            <div className="search-wrap" style={{
              flex: 1, display: "flex", alignItems: "center", gap: 8,
              background: HEART.offwhite,
              borderRadius: 12,
              border: `1.5px solid ${HEART.border}`,
              padding: "11px 16px",
            }}>
              <Search size={15} color={HEART.textFaint} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search products…"
                style={{
                  flex: 1, background: "none", border: "none", outline: "none",
                  fontSize: 14, color: HEART.text, fontFamily: "'DM Sans',sans-serif",
                }}
              />
            </div>
            <button className="filter-btn" style={{
              display: "flex", alignItems: "center", gap: 7,
              background: HEART.cardBg,
              border: `1.5px solid ${HEART.border}`,
              borderRadius: 12, padding: "11px 18px",
              color: HEART.red, fontWeight: 700, fontSize: 13,
              cursor: "pointer", flexShrink: 0,
              fontFamily: "'DM Sans',sans-serif",
            }}>
              <SlidersHorizontal size={14} color={HEART.red} /> Filter
            </button>
          </div>
        </div>
      </div>

      {/* ══ PRODUCT LIST ══ */}
      <div style={{ padding: "16px 16px 0" }}>
        {filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ textAlign: "center", paddingTop: 80 }}
          >
            {/* Empty state icon — squared rounded box, not circle */}
            <div style={{
              width: 78, height: 78, borderRadius: 20, margin: "0 auto 20px",
              background: GRAD_CTA,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 10px 28px rgba(200,32,46,.22)",
            }}>
              <Package size={34} color="#fff" />
            </div>
            <h2 style={{
              fontWeight: 900, fontSize: 20, color: HEART.text, marginBottom: 8,
              fontFamily: "'Sora',sans-serif",
            }}>
              {products.length === 0 ? "No products yet" : "No results found"}
            </h2>
            <p style={{
              color: HEART.textMuted, marginBottom: 28, fontSize: 14,
              lineHeight: 1.7, maxWidth: 280, margin: "0 auto 28px",
            }}>
              {products.length === 0
                ? "Add your first product to start selling on Heartlas."
                : "Try adjusting your search or filter."}
            </p>
            {products.length === 0 && (
              <motion.button
                whileTap={{ scale: 0.96 }}
                onClick={() => goToAdd()}
                style={{
                  padding: "13px 32px", borderRadius: 12, border: "none",
                  background: GRAD_CTA,
                  color: "#fff", fontWeight: 700, fontSize: 14,
                  cursor: "pointer", fontFamily: "'Sora',sans-serif",
                  boxShadow: "0 6px 20px rgba(200,32,46,.26)",
                }}
              >
                + Add First Product
              </motion.button>
            )}
          </motion.div>
        ) : (
          <AnimatePresence>
            {filtered.map((product: Product) => {
              const sum    = ratingSummaries[product.listingId];
              const rating = sum && sum.totalReviews > 0
                ? { avg: sum.avgRating, count: sum.totalReviews }
                : null;
              return (
                <ProductRow
                  key={product.listingId}
                  product={product}
                  rating={rating}
                  onEdit={() => goToAdd(product.listingId)}
                  onDelete={() => handleDelete(product.listingId)}
                />
              );
            })}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default Products;